import React, { useState, useEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import showdown from 'showdown';
import './Documentation.css';

const Documentation = () => {
  const location = useLocation();
  const history = useHistory();
  const [currentContent, setCurrentContent] = useState('<h1>TestingHub User Guide</h1><p>Loading documentation...</p>');
  const [currentPage, setCurrentPage] = useState('README');
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // Dynamic title generation function (shared with breadcrumb)
  const generateTitle = (filePath) => {
    // Convert kebab-case filename to Title Case
    const convertToTitleCase = (str) => {
      return str
        .replace(/-/g, ' ')
        .replace(/\b\w/g, l => l.toUpperCase())
        // Handle special cases for better readability
        .replace(/\bApi\b/g, 'API')
        .replace(/\bFaq\b/g, 'FAQ')
        .replace(/\bUi\b/g, 'UI')
        .replace(/\bId\b/g, 'ID')
        .replace(/\bUrl\b/g, 'URL')
        .replace(/\bHtml\b/g, 'HTML')
        .replace(/\bCss\b/g, 'CSS')
        .replace(/\bJs\b/g, 'JS')
        .replace(/\bTestinghub\b/g, 'TestingHub')
        .replace(/\bBuysell\b/g, 'Buy/Sell');
    };

    // Get the last part of the path (filename without extension)
    const filename = filePath.split('/').pop();

    // Convert to title case
    let title = convertToTitleCase(filename);

    // Add contextual prefixes based on folder structure
    const pathParts = filePath.split('/');

    // Add "How to" prefix for hosting guides
    if (pathParts.includes('hosting') &&
      !title.toLowerCase().startsWith('how to') &&
      !title.toLowerCase().includes('dashboard') &&
      !title.toLowerCase().includes('request') &&
      !title.toLowerCase().includes('testinghub') &&
      !title.toLowerCase().startsWith('what is')) {
      title = `How to ${title}`;
    }

    // Add "Getting Started on" prefix for getting-started section
    if (pathParts.includes('getting-started')) {
      const section = convertToTitleCase(filename);
      title = `Getting Started on ${section}`;
    }

    // Handle special naming patterns
    if (filename === 'whats-new') {
      title = "What's New";
    }

    return title;
  };

  // Navigation structure - now using dynamic titles
  // To add new documents:
  // 1. Create your .md file in the appropriate folder under /public/docs/
  // 2. Add the file path (without .md extension) to the appropriate section below
  // 3. The title will be automatically generated from the filename and folder structure
  // 4. No code changes needed for titles - they're generated dynamically!
  const navigation = [
    {
      title: 'Get Started',
      items: [
        { file: 'getting-started/hosting' },
        { file: 'getting-started/test-data' }
      ]
    },
    {
      title: 'Release Notes',
      items: [
        { file: 'release-notes/whats-new' }
      ]
    },
    {
      title: 'Test Data',
      items: [
        {
          file: 'features/test-data/new-buying-experience',
          subitems: [
            { file: 'features/test-data/new-buying-experience/subscription-management' },
            { file: 'features/test-data/new-buying-experience/agency' },
            { file: 'features/test-data/new-buying-experience/sales' },
            { file: 'features/test-data/new-buying-experience/direct-estore' }
          ]
        },
        { file: 'features/test-data/data-store' },
        { file: 'features/test-data/products' },
        { file: 'features/test-data/accounts-contacts' },
        { file: 'features/test-data/buy-sell' },
      ]
    },
    {
      title: 'Hosting',
      items: [
        { file: 'features/hosting/what-is-test-hosting' },
        { file: 'features/hosting/onboard' },
        { file: 'features/hosting/adapting-testinghub-framework' },
        {
          file: 'features/hosting/test-management',
          title: 'Test Management',
          subitems: [
            { file: 'features/hosting/create-project' },
            { file: 'features/hosting/create-job-or-testcase' },
            { file: 'features/hosting/manage-test-data' },
            { file: 'features/hosting/run-job' },
            { file: 'features/hosting/test-suites', title: 'Test Suites' }
          ]
        },
        { file: 'features/hosting/view-results', title: 'How to View Results in Jobs Queue' },
        { file: 'features/hosting/ai-analysis', title: 'AI Analysis' },
        { file: 'features/hosting/dashboard', title: 'Dashboard' }
      ]
    },
    {
      title: 'Hosting APIs',
      items: [
        { file: 'apis/get-access', title: 'Get Auth' },
        {
          file: 'apis/test-management',
          title: 'Test Management',
          subitems: [
            { file: 'apis/get-test-cases' },
            { file: 'apis/run-job' },
            { file: 'apis/run-test-suite' },
            { file: 'apis/get-status' }
          ]
        }
      ]
    },
    {
      title: 'Support',
      items: [
        { file: 'support/help' }
      ]
    }
  ];

  // Create 404 page content
  const create404Page = () => {
    return `
      <div class="error-page">
        <div class="error-content">
          <div class="error-icon">📄</div>
          <h1>404 - Page Not Found</h1>
          <p>The documentation page you're looking for doesn't exist or may have been moved.</p>
          
          <div class="error-actions">
            <button class="error-button primary" onclick="window.location.href='/documentation'">
              🏠 Go to Home
            </button>
            <button class="error-button secondary" onclick="window.history.back()">
              ← Go Back
            </button>
          </div>
          
          <div class="error-suggestions">
            <h3>Popular Documentation Pages:</h3>
            <ul>
              <li><a href="/documentation/getting-started/hosting">Getting Started with Hosting</a></li>
              <li><a href="/documentation/getting-started/test-data">Getting Started with Test Data</a></li>
              <li><a href="/documentation/features/hosting/onboard">How to Onboard</a></li>
              <li><a href="/documentation/features/hosting/create-project">How to Create Project</a></li>
              <li><a href="/documentation/features/test-data/new-buying-experience">New Buying Experience</a></li>
            </ul>
          </div>
          
          <div class="error-help">
            <p>Still can't find what you're looking for? <a href="/documentation/support/help">Contact Support</a></p>
          </div>
        </div>
      </div>
    `;
  };

  // Create general error page content
  const createErrorPage = () => {
    return `
      <div class="error-page">
        <div class="error-content">
          <div class="error-icon">⚠️</div>
          <h1>Oops! Something went wrong</h1>
          <p>We encountered an error while loading the documentation content.</p>
          
          <div class="error-actions">
            <button class="error-button primary" onclick="window.location.reload()">
              🔄 Reload Page
            </button>
            <button class="error-button secondary" onclick="window.location.href='/documentation'">
              🏠 Go to Home
            </button>
          </div>
          
          <div class="error-help">
            <p>If this problem persists, please <a href="/documentation/support/help">contact our support team</a>.</p>
          </div>
        </div>
      </div>
    `;
  };

  // Load markdown content
  const loadMarkdownContent = async (filename, showLoading = true) => {
    if (showLoading) {
      setLoading(true);
    }
    try {
      const response = await fetch(`/docs/${filename}.md`);
      if (response.ok) {
        const markdownContent = await response.text();

        // Check if we got HTML instead of markdown (indicating server fallback)
        if (markdownContent.trim().startsWith('<!DOCTYPE html>')) {
          const errorContent = create404Page();
          setCurrentContent(errorContent);
          setCurrentPage('404');
        } else {
          const htmlContent = parseMarkdown(markdownContent);
          setCurrentContent(htmlContent);
          setCurrentPage(filename);
        }
      } else {
        const errorContent = create404Page();
        setCurrentContent(errorContent);
        setCurrentPage('404');
      }
    } catch (error) {
      const errorContent = createErrorPage();
      setCurrentContent(errorContent);
      setCurrentPage('error');
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  // Note: Internal link clicks are now handled directly in the markdownOptions.overrides.a component

  // Extract file path from URL
  const getFilePathFromURL = () => {
    const pathname = location.pathname;
    if (pathname === '/documentation' || pathname === '/documentation/') {
      return 'README';
    }
    // Remove '/documentation/' prefix to get the file path
    const filePath = pathname.replace('/documentation/', '');
    return filePath || 'README';
  };

  // Update URL when navigating
  const updateURL = (filename) => {
    const url = filename === 'README' ? '/documentation' : `/documentation/${filename}`;
    history.push(url);
  };

  // Load initial content based on URL
  useEffect(() => {
    const filePathFromURL = getFilePathFromURL();
    setCurrentPage(filePathFromURL);
    loadMarkdownContent(filePathFromURL, false); // Don't show loading spinner for initial load
  }, []);

  // Handle URL changes (browser back/forward)
  useEffect(() => {
    const filePathFromURL = getFilePathFromURL();
    if (filePathFromURL !== currentPage) {
      setCurrentPage(filePathFromURL);
      loadMarkdownContent(filePathFromURL, false);
    }
  }, [location.pathname]);

  // Handle clicks on internal documentation links
  const handleInternalLinkClick = (event) => {
    if (event.target.classList.contains('doc-internal-link')) {
      event.preventDefault();
      const filename = event.target.getAttribute('data-file');
      if (filename) {
        // Remove .md extension for our loadMarkdownContent function
        const cleanFilename = filename.replace('.md', '');
        handleNavClick(cleanFilename); // Use handleNavClick to update URL too
      }
    }
  };

  // Set up click handler for internal links
  useEffect(() => {
    document.addEventListener('click', handleInternalLinkClick);
    return () => {
      document.removeEventListener('click', handleInternalLinkClick);
    };
  }, []);

  // Handle navigation click with URL update
  const handleNavClick = (filename) => {
    loadMarkdownContent(filename);
    updateURL(filename);
  };

  // Configure Showdown with proper options
  const createMarkdownParser = () => {
    const converter = new showdown.Converter({
      tables: true,                // Enable GitHub tables
      strikethrough: true,         // Enable strikethrough
      tasklists: true,            // Enable task lists
      ghCodeBlocks: true,         // Enable GitHub code blocks
      smoothLivePreview: true,    // Enable smooth live preview
      simplifiedAutoLink: true,   // Enable simplified auto links
      excludeTrailingPunctuationFromURLs: true,
      literalMidWordUnderscores: true,
      simpleLineBreaks: true,     // Convert \n to <br>
      requireSpaceBeforeHeadingText: true,
      ghMentions: false,          // Disable GitHub mentions
      emoji: false,               // Disable emoji conversion
      underline: false,           // Disable underline conversion
      completeHTMLDocument: false,
      metadata: false,
      splitAdjacentBlockquotes: true,
      headerLevelStart: 1,        // Start headers at h1
      parseImgDimensions: true,   // Parse image dimensions
      openLinksInNewWindow: true, // Open external links in new window
    });

    return converter;
  };

  // Post-process HTML to handle internal links and images
  const postProcessHTML = (html) => {
    // Convert internal .md links to buttons
    html = html.replace(/<a\s+href="([^"]*\.md)"([^>]*)>(.*?)<\/a>/gi, (match, href, attrs, text) => {
      const filename = href.replace('.md', '');
      const titleMatch = attrs.match(/title="([^"]*)"/);
      const title = titleMatch ? titleMatch[1] : '';
      return `<button class="doc-internal-link" data-file="${filename}" title="${title}">${text}</button>`;
    });

    // Fix image paths - convert relative paths to absolute paths
    html = html.replace(/<img\s+([^>]*?)src="([^"]*)"([^>]*?)>/gi, (match, beforeSrc, src, afterSrc) => {
      let fixedSrc = src;

      // Handle relative paths that start with ../
      if (src.startsWith('../')) {
        // Remove all ../ and prepend /docs/
        fixedSrc = '/docs/' + src.replace(/\.\.\//g, '');
      }
      // Handle relative paths that don't start with / or http
      else if (!src.startsWith('/') && !src.startsWith('http')) {
        fixedSrc = '/docs/' + src;
      }

      return `<img ${beforeSrc}src="${fixedSrc}"${afterSrc}>`;
    });

    // Ensure external links have proper attributes
    html = html.replace(/<a\s+href="([^"]*)"([^>]*)>(.*?)<\/a>/gi, (match, href, attrs, text) => {
      if (!href.startsWith('#') && !href.startsWith('mailto:') && !attrs.includes('target=')) {
        return `<a href="${href}" target="_blank" rel="noopener noreferrer"${attrs}>${text}</a>`;
      }
      return match;
    });

    return html;
  };

  // Professional markdown parser using Showdown library
  const parseMarkdown = (markdown) => {
    const converter = createMarkdownParser();
    let html = converter.makeHtml(markdown);
    html = postProcessHTML(html);
    return html;
  };


  // Comprehensive search through all documents
  const performComprehensiveSearch = async (searchTerm) => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    const results = [];

    // Collect all files from navigation structure
    const getAllFiles = (items) => {
      let files = [];
      items.forEach(item => {
        if (item.file) {
          files.push({
            file: item.file,
            title: item.title || generateTitle(item.file),
            section: item.section || 'Documentation'
          });
        }
        if (item.items) {
          files = files.concat(getAllFiles(item.items));
        }
        if (item.subitems) {
          files = files.concat(getAllFiles(item.subitems));
        }
      });
      return files;
    };

    // Add README file
    const allFiles = [
      { file: 'README', title: 'Home', section: 'Home' },
      ...getAllFiles(navigation.flatMap(section =>
        section.items.map(item => ({ ...item, section: section.title }))
      ))
    ];

    // Search through each document
    for (const fileInfo of allFiles) {
      try {
        const response = await fetch(`/docs/${fileInfo.file}.md`);
        if (response.ok) {
          const content = await response.text();
          const lowerContent = content.toLowerCase();
          const lowerSearchTerm = searchTerm.toLowerCase();

          // Check if search term exists in content
          if (lowerContent.includes(lowerSearchTerm)) {
            // Find all occurrences and their context
            const lines = content.split('\n');
            const matches = [];

            lines.forEach((line, index) => {
              if (line.toLowerCase().includes(lowerSearchTerm)) {
                // Get context (line before and after)
                const contextStart = Math.max(0, index - 1);
                const contextEnd = Math.min(lines.length - 1, index + 1);
                const context = lines.slice(contextStart, contextEnd + 1);

                matches.push({
                  lineNumber: index + 1,
                  line: line.trim(),
                  context: context
                });
              }
            });

            if (matches.length > 0) {
              results.push({
                file: fileInfo.file,
                title: fileInfo.title,
                section: fileInfo.section,
                matches: matches,
                matchCount: matches.length
              });
            }
          }
        }
      } catch (error) {
        console.log(`Could not search in ${fileInfo.file}:`, error);
      }
    }

    setSearchResults(results);
    setIsSearching(false);
  };

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm) {
        performComprehensiveSearch(searchTerm);
      } else {
        setSearchResults([]);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Find the full navigation path for a file
  const findNavigationPath = (filePath, navItems = navigation, path = []) => {
    for (const section of navItems) {
      const currentPath = [...path, section.title];

      if (section.items) {
        for (const item of section.items) {
          if (item.file === filePath) {
            return [...currentPath, item.title || generateTitle(item.file)];
          }

          if (item.subitems) {
            const itemPath = [...currentPath, item.title];
            for (const subitem of item.subitems) {
              if (subitem.file === filePath) {
                return [...itemPath, subitem.title || generateTitle(subitem.file)];
              }
            }
          }
        }
      }
    }
    return [generateTitle(filePath)]; // Fallback to just the file title
  };

  // Convert file path to display title for breadcrumb - uses shared generateTitle function
  const getBreadcrumbTitle = (filePath) => {
    if (filePath === 'README') return 'Home';
    return generateTitle(filePath);
  };

  // Get full breadcrumb path for nested navigation
  const getBreadcrumbPath = (filePath) => {
    if (filePath === 'README') return ['Home'];
    if (filePath === '404') return ['404 - Page Not Found'];
    if (filePath === 'error') return ['Error Loading Content'];
    return findNavigationPath(filePath);
  };

  // Filter navigation items based on search
  const filterNavItems = (items, searchTerm) => {
    if (!searchTerm) return items;

    return items.filter(item => {
      // Check if current item matches - use dynamic title generation
      const itemTitle = item.title || (item.file ? generateTitle(item.file) : '');
      const itemMatches = itemTitle.toLowerCase().includes(searchTerm.toLowerCase());

      // Check if any subitems match - use dynamic title generation
      const hasMatchingSubitems = (item.items && item.items.some(subItem => {
        const subItemTitle = subItem.title || (subItem.file ? generateTitle(subItem.file) : '');
        return subItemTitle.toLowerCase().includes(searchTerm.toLowerCase());
      })) || (item.subitems && item.subitems.some(subItem => {
        const subItemTitle = subItem.title || (subItem.file ? generateTitle(subItem.file) : '');
        return subItemTitle.toLowerCase().includes(searchTerm.toLowerCase());
      }));

      return itemMatches || hasMatchingSubitems;
    }).map(item => {
      // If item has subitems, filter them too
      const filteredItem = { ...item };
      if (item.items) {
        filteredItem.items = filterNavItems(item.items, searchTerm);
      }
      if (item.subitems) {
        filteredItem.subitems = filterNavItems(item.subitems, searchTerm);
      }
      return filteredItem;
    });
  };

  // Render navigation items
  const renderNavItems = (items, level = 0) => {
    return items.map((item, index) => (
      <div key={index} className={`nav-item level-${level}`}>
        {item.file ? (
          // Item has a file, so it's clickable
          <button
            className={`nav-link ${currentPage === item.file ? 'active' : ''}`}
            onClick={() => handleNavClick(item.file)}
          >
            {item.title || generateTitle(item.file)}
          </button>
        ) : (
          // Item doesn't have a file, so it's just a section title
          <div className="nav-section-title">{item.title}</div>
        )}

        {/* Render subitems if they exist (regardless of whether parent has file) */}
        {item.subitems && (
          <div className="nav-subitems">
            {renderNavItems(item.subitems, level + 1)}
          </div>
        )}

        {/* Render items if they exist (for main sections) */}
        {item.items && (
          <div className="nav-subitems">
            {renderNavItems(item.items, level + 1)}
          </div>
        )}
      </div>
    ));
  };

  return (
    <div className="documentation-container">
      {/* Main Content Wrapper */}
      <div className="documentation-main">
        {/* Sidebar Navigation */}
        <div className="documentation-sidebar">
          <div className="sidebar-header">
            <h2> TestingHub Documentation</h2>
          </div>

          <div className="search-box">
            <div className="search-icon">🔍</div>
            <input
              type="text"
              placeholder="Search documentation..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="sidebar-content">
            <button
              className={`nav-link  ${currentPage === 'README' ? 'active' : ''}`}
              onClick={() => handleNavClick('README')}
            >
              🏠 Home
            </button>

            {searchTerm ? (
              <div className="search-results">
                {isSearching ? (
                  <div className="search-loading">
                    <div className="loading-spinner"></div>
                    <p>Searching...</p>
                  </div>
                ) : searchResults.length > 0 ? (
                  <>
                    <div className="search-results-header">
                      Found {searchResults.reduce((total, result) => total + result.matchCount, 0)} matches in {searchResults.length} documents
                    </div>
                    {searchResults.map((result, index) => (
                      <div key={index} className="search-result-item">
                        <button
                          className={`search-result-link ${currentPage === result.file ? 'active' : ''}`}
                          onClick={() => handleNavClick(result.file)}
                        >
                          <div className="search-result-title">{result.title}</div>
                          <div className="search-result-section">{result.section}</div>
                          <div className="search-result-matches">{result.matchCount} matches</div>
                        </button>
                        <div className="search-result-preview">
                          {result.matches.slice(0, 2).map((match, matchIndex) => (
                            <div key={matchIndex} className="search-match">
                              <div
                                className="search-match-line"
                                dangerouslySetInnerHTML={{
                                  __html: match.line.replace(
                                    new RegExp(`(${searchTerm})`, 'gi'),
                                    '<mark>$1</mark>'
                                  )
                                }}
                              />
                            </div>
                          ))}
                          {result.matches.length > 2 && (
                            <div className="search-more-matches">
                              +{result.matches.length - 2} more matches
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </>
                ) : (
                  <div className="search-no-results">
                    No results found for "{searchTerm}"
                  </div>
                )}
              </div>
            ) : (
              <div className="navigation-tree">
                {filterNavItems(navigation, searchTerm).map((section, index) => (
                  <div key={index} className="nav-section">
                    <div className="nav-section-title">{section.title}</div>
                    <div className="nav-section-items">
                      {renderNavItems(section.items)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Reading Progress Bar */}
        <div className="reading-progress" style={{ width: '0%' }}></div>

        {/* Main Content */}
        <div className="documentation-content">
          {loading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading documentation...</p>
            </div>
          ) : (
            <>
              {/* Breadcrumb Navigation */}
              <div className="breadcrumb">
                <span className="breadcrumb-item">TestingHub</span>
                <span className="breadcrumb-item">Documentation</span>
                {getBreadcrumbPath(currentPage).map((pathItem, index, array) => (
                  <span
                    key={index}
                    className={`breadcrumb-item ${index === array.length - 1 ? 'active' : ''}`}
                  >
                    {pathItem}
                  </span>
                ))}
              </div>

              <div
                className="markdown-content"
                dangerouslySetInnerHTML={{ __html: currentContent }}
              />
            </>
          )}
        </div>
      </div>

    </div>
  );
};

export default Documentation;
